import * as ko from 'knockout';

import {ObjectTypeId, SPINNER} from '../../../../constants';

import {IMessageData, IObjectData} from '../../../../index';

import {IDialogOptions} from '../../../dialog/index';

import {ButtonManager} from '../../../../util/ButtonManager';

import {getCancelButton, getSubmitButton} from '../../../../constants/RouteDefinitions';

import PromiseRequest from '../../../../req/PromiseRequest';

import {KOSubscriptionTracker} from '../../../../util/KOSubscriptionTracker';

import {nsGroupMemberOverride} from '../nsGroupMemberOverride';

import {createErrorString, isPartOf} from '../../../../util/misc';

import {nsLanguage} from '../../../../util/nsLanguage';

import {global} from '../../../../appContext';

import runMembershipAddMembersBatch = nsGroupMemberOverride.runMembershipAddMembersBatch;

import IMembershipAddMemberFailure = nsGroupMemberOverride.IMembershipAddMemberFailure;

import isMembershipAddMemberAlreadyExistsError = nsGroupMemberOverride.isMembershipAddMemberAlreadyExistsError;
import add_membersHtml from './add-members.html?raw';



export const template = add_membersHtml;



interface IParams {

    dialog: IDialogOptions;

    group: IObjectData;

    overrideTargetUserIds: number[];

    onComplete: () => void | Promise<void>;

}



enum Step {

    Pick,

    Process,

    Summary,

}



/** Wizard: pick users, create membership overrides, then show a per-item result summary. */

export class viewModel {

    readonly Step = Step;

    private readonly kosub = new KOSubscriptionTracker();

    private readonly reqCreateOverride = new PromiseRequest('createoverride');

    private readonly onComplete: () => void | Promise<void>;

    private pendingUsers: IObjectData[] = [];



    readonly messageData = ko.observable<IMessageData>(null);

    readonly selectedObjects = ko.observableArray<IObjectData>([]);

    readonly pageButtons = new ButtonManager();

    readonly overrideTargetUserIds: number[];

    readonly group: IObjectData;

    readonly step = ko.observable<Step>(Step.Pick);

    readonly percentComplete = ko.observable<number>(null);

    readonly succeededUsers = ko.observableArray<IObjectData>([]);

    readonly existsUsers = ko.observableArray<IMembershipAddMemberFailure>([]);

    readonly errorUsers = ko.observableArray<IMembershipAddMemberFailure>([]);



    readonly isStep = isPartOf.bind(this, this.step);

    readonly createErrorString = createErrorString;



    constructor(private readonly params: IParams) {

        this.group = params.group;

        this.overrideTargetUserIds = params.overrideTargetUserIds;

        this.onComplete = params.onComplete;

        this.initButtons();

        this.kosub.subscribe(this.selectedObjects, () => this.updateSubmitButton());

        this.kosub.subscribe(this.step, step => this.onStepChange(step));

    }



    private initButtons(): void {

        this.pageButtons.add(getCancelButton(() => this.params.dialog.doClose?.(), {

            validator: () => this.isStep(Step.Pick),

        }));

        this.pageButtons.add(getSubmitButton({

            label: 'add',

            icon: 'fa-plus',

            onClick: () => this.onAddClick(),

            enabled: () => !!this.selectedObjects().length,

            validator: () => this.isStep(Step.Pick),

        }));

        this.pageButtons.add({

            onClick: () => this.params.dialog.doClose?.(),

            label: 'close',

            icon: 'fa-times',

            validator: () => this.isStep(Step.Summary),

        });

    }



    /** Re-evaluate submit button when the selection changes. */

    private updateSubmitButton(): void {

        this.pageButtons.buttons?.valueHasMutated?.();

    }



    private onAddClick(): void {

        const users = this.selectedObjects();

        if (!users.length || !this.isStep(Step.Pick)) {

            return;

        }

        this.pendingUsers = users.slice();

        this.succeededUsers([]);

        this.existsUsers([]);

        this.errorUsers([]);

        this.step(Step.Process);

    }



    private onStepChange(step: Step): void {

        switch (step) {

            case Step.Process:

                this.params.dialog.isClosable?.(false);

                this.startProcessing();

                break;

            case Step.Summary:

                this.params.dialog.isClosable?.(true);

                this.finishSummary();

                break;

        }

    }



    /** Runs sequential `createoverride` calls and advances to the summary step. */

    private startProcessing(): void {

        const users = this.pendingUsers;

        const total = users.length;

        this.percentComplete(0);

        this.updateProcessMessage(0, total);

        runMembershipAddMembersBatch(this.reqCreateOverride, this.group, users, {

            onProgress: (processed, numTotal) => {

                this.percentComplete(numTotal ? processed / numTotal : null);

                this.updateProcessMessage(processed, numTotal);

            },

            onComplete: (successes, failures) => {

                this.succeededUsers(successes);

                const exists: IMembershipAddMemberFailure[] = [];

                const errors: IMembershipAddMemberFailure[] = [];

                for (const failure of failures) {

                    if (isMembershipAddMemberAlreadyExistsError(failure.error)) {

                        exists.push(failure);

                    } else {

                        errors.push(failure);

                    }

                }

                this.existsUsers(exists);

                this.errorUsers(errors);

                this.messageData(null);

                this.percentComplete(null);

                this.step(Step.Summary);

            },

        });

    }



    /** Processing message with completed count for the current batch. */

    private updateProcessMessage(processed: number, total: number): void {

        this.messageData({message: `((creating|td)) [${processed}/${total}] ${SPINNER}`});

    }



    /** After summary is shown: refresh host data and optional snackbar for successes. */

    private finishSummary(): void {

        void Promise.resolve(this.onComplete());

        const n = this.succeededUsers().length;

        if (n) {
            global.snackbarData({message: this.getAddedSubjectMessage(n)});
        }
    }

    successSummaryMessage(): string {
        return this.getSummaryMessage('group.members.add.summary.success', this.succeededUsers().length);
    }

    existsSummaryMessage(): string {
        return this.getSummaryMessage('group.members.add.summary.alreadyexists', this.existsUsers().length);
    }

    errorSummaryMessage(): string {
        return this.getSummaryMessage('group.members.add.summary.error', this.errorUsers().length);
    }

    /** Summary line with translation key and item count, e.g. `((key)) [3]`. */
    private getSummaryMessage(key: string, num: number): string {
        return `((${key})) [${num}]`;
    }

    /** `added subject` with localized `s` (e.g. "5 users"). */
    private getAddedSubjectMessage(num: number): string {
        return `added subject|s:((${this.getNumUsersLabel(num)}))`;
    }



    private getNumUsersLabel(num: number): string {

        return nsLanguage.getNumObjectsLabel(num, ObjectTypeId.User, false);

    }



    dispose(): void {

        this.kosub.unsuscribeAll();

        this.reqCreateOverride.abort();

        this.params.dialog.isClosable?.(true);

    }

}


